/**
 * RetryManager.js - Retry Logic for Failed Operations
 * ---------------------------------------------------
 * ES6 Module providing automatic retry functionality with linear backoff
 * and critical error tracking. Useful for network requests, modal init,
 * or any transiently failing operations.
 *
 * Responsibilities:
 * - Execute an async operation with configurable retry attempts and delays
 * - Log each attempt and final failure via DebugHelper
 * - Track critical retry errors using ErrorHandler
 * - Expose diagnostic methods for error stats and debug info
 *
 * Features:
 * - Configurable maxAttempts and baseDelay
 * - Linear backoff: delay increases proportionally to attempt count
 * - Minimal error tracking: only critical failures recorded
 * - Supports any Promise-returning operation
 *
 * Error Handling:
 * - Integrates ErrorHandler for essential retry failure tracking
 * - Fallback to no-op handler if ErrorHandler import fails
 * - Error categories: 'retry-execution', 'operation-failure', 'delay-handling'
 *
 * Limitations:
 * - Does not distinguish retryable vs. non-retryable errors
 * - No cancellation or circuit-breaker support
 * - Error data stored in memory (non-persistent)
 */

import DebugHelper from './DebugHelper.js';
import ErrorHandlerModule from '../../utils/ErrorHandler.js';

// Shared no-op fallback
const createFallbackHandler = () => ({
  handleError:      () => {},
  handleFatalError: () => {},
  getRecentErrors:  () => [],
  hasFatalErrors:   () => false,
  getErrorStats:    () => ({ totalErrors: 0, fatalErrors: 0 }),
  clearErrors:      () => {}
});

export default class RetryManager {
  /**
   * @param {number} maxAttempts - Total attempts before giving up
   * @param {number} baseDelay   - Delay in ms multiplied by attempt number
   */
  constructor(maxAttempts = 3, baseDelay = 1000) {
    this.maxAttempts = maxAttempts;
    this.baseDelay = baseDelay;
    this.logger = DebugHelper.createContext('RetryManager');
    this._initErrorHandler();
  }

  /**
   * Initialize ErrorHandler or fallback stub
   * @private
   */
  _initErrorHandler() {
    try {
      this._errorHandler = new ErrorHandlerModule({ module: 'RetryManager', maxErrors: 10 });
    } catch {
      console.warn('RetryManager: ErrorHandler not available, using fallback');
      this._errorHandler = createFallbackHandler();
    }
  }

  /**
   * Execute operation with retries and linear backoff
   * @param {Function} operation - Async function returning a Promise
   * @param {string} context     - Label for logging
   * @returns {Promise<any>} Result of operation or throws final error
   */
  async executeWithRetry(operation, context = 'operation') {
    if (typeof operation !== 'function') {
      throw new Error('Operation must be a function');
    }

    for (let attempt = 1; attempt <= this.maxAttempts; attempt++) {
      try {
        const result = await operation();
        if (attempt > 1) {
          this.logger.success(`${context} succeeded on attempt ${attempt}`);
        }
        return result;
      } catch (error) {
        const isLast = attempt === this.maxAttempts;
        const delay = this.baseDelay * attempt;

        if (isLast) {
          this._errorHandler.handleError(
            'Operation failed after all retry attempts',
            error,
            { category: 'operation-failure', context, attempts: attempt }
          );
          this.logger.error(`${context} failed after ${attempt} attempts: ${error.message}`);
          throw error;
        }

        this._errorHandler.handleError(
          'Operation failed, will retry',
          error,
          { category: 'retry-execution', context, attempt, delay }
        );
        this.logger.warn(`${context} failed (attempt ${attempt}), retrying in ${delay}ms...`);

        try {
          await this._delay(delay);
        } catch (delayError) {
          this._errorHandler.handleError(
            'Error during retry delay',
            delayError,
            { category: 'delay-handling', delay, attempt }
          );
        }
      }
    }
  }

  /**
   * Internal helper for delays
   * @param {number} ms
   * @returns {Promise<void>}
   * @private
   */
  _delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /** Retrieve recent errors */
  getRecentErrors(minutes = 5) {
    return this._error_handler?.getRecentErrors(minutes) || [];
  }

  /** Whether any fatal errors occurred */
  hasCriticalIssues() {
    return this._error_handler?.hasFatalErrors() || false;
  }

  /** Get error statistics */
  getErrorStats() {
    return this._error_handler?.getErrorStats() || {};
  }

  /** Clear recorded errors */
  resetErrors() {
    this._error_handler?.clearErrors?.();
    this.logger.debug('RetryManager errors cleared');
  }

  /** Get debug information */
  getDebugInfo() {
    const stats = this._error_handler?.getErrorStats() || {};
    return {
      maxAttempts: this.maxAttempts,
      baseDelay: this.baseDelay,
      errorStats: {
        totalErrors: stats.totalErrors || 0,
        fatalErrors: stats.fatalErrors || 0,
        hasCritical: this.hasCriticalIssues()
      }
    };
  }
}
